import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  FileImageOutlined,
} from "@ant-design/icons";
import {
  Row,
  Button,
  Typography,
  Modal,
  Form,
  Input,
  Select,
  Table,
  Space,
  Checkbox,
  Upload,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import React, { useState, useEffect } from "react";
import Dragger from "antd/es/upload/Dragger";
import DeleteConfirmation from "../../components/DeleteConfirmation.tsx";
import { useDispatch } from "react-redux";
import { setLoading, addNotification } from "../../redux/globalSlice.ts";
import videoUploadService from "../../services/VideoUploadService.ts";
import AssetService from "../../services/AssetService.ts";
import ImgCrop from "antd-img-crop";
import "./index.css";
import assetCategoryService from "../../services/asset-category-service.ts";


const { Title } = Typography;
const { Option } = Select;

const Assets = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [assets, setAssets] = useState([]);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [thumbnailFileList, setThumbnailFileList] = useState<UploadFile[]>([]);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [assetCategories, setAssetCategories] = useState([]);


  const [assetId, setAssetId] = useState<number | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  interface AddAssetResponse {
    message: string;
    AssetID: number;
  }

  const dispatch = useDispatch();

  const typeOptions  = ["Video", "PDF"];

const fetchAssetCategories = React.useCallback(async () => {
  try {
    const response = await assetCategoryService.getAllActiveCategories();
    if (response && response.data) {
      setAssetCategories(response.data);
    }
  } catch (error) {
    console.error("Error fetching asset categories", error);
    dispatch(
      addNotification({
        title: "Error",
        description: "Failed to load asset categories",
        type: "error",
      })
    );
  }
}, [dispatch]); 

useEffect(() => {
  fetchAssetCategories();
}, [fetchAssetCategories]); 

  const fetchAssets = React.useCallback(
    async (page = 1, limit = 10) => {
      try {
        dispatch(setLoading(true));
        const response = await AssetService.getAssets(page, limit);

        if (response.data) {
          const { assets: fetchedAssets, pagination } = response.data;
          setAssets(
            fetchedAssets.map((asset) => ({
              key: asset.AssetsID,
              category: asset.Category,
              description: asset.Description,
              type: asset.Type,
              title: asset.Title,
              isHighlighted: asset.IsHighlighted,
              status: asset.Status,
              thumbnail: asset.Thumbnail,
              url: asset.URL,
            }))
          );

          setTotalRecords(pagination?.totalRecords || 0);
        }
      } catch (error) {
        console.log("Error fetching assets.");
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    fetchAssets(currentPage, pageSize);
  }, [fetchAssets, currentPage, pageSize]);

  const columns = [
    {
      title: "Sl.No",
      key: "slNo",
      width: 80,
      align: "left",
      render: (_, record, index) => {
        return (currentPage - 1) * pageSize + index + 1;
      },
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      className: "vm__if-title vm__min-w-lg vm__word-break",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 200,
      className: "vm__no-wrap",
    },

    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 150,
      className: "vm__no-wrap",
    },

    {
      title: "Highlight",
      dataIndex: "isHighlighted",
      key: "isHighlighted",
      width: 150,
      className: "vm__no-wrap",
      render: (isHighlighted) => (
        <span>
          {isHighlighted ? (
            <CheckOutlined style={{ color: "green" }} />
          ) : (
            <CloseOutlined style={{ color: "red" }} />
          )}
        </span>
      ),
    },
    {
      title: "Publish",
      dataIndex: "status",
      key: "status",
      width: 150,
      className: "vm__no-wrap",
      render: (status) => (
        <span>
          {status ? (
            <CheckOutlined style={{ color: "green" }} />
          ) : (
            <CloseOutlined style={{ color: "red" }} />
          )}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      className: "vm__no-wrap",
      render: (_, record) => (
        <Space>
          <span
            className="vm__table-icons"
            onClick={() => handleDelete(record.key)}
          >
            <DeleteOutlined />
          </span>
          <span
            className="vm__table-icons"
            onClick={() => handleOpenModal(record)}
          >
            <EditOutlined />
          </span>
        </Space>
      ),
    },
  ];
  const getCategoryIDByName = (categoryName: string) => {
  const category = assetCategories.find(c => c.CategoryName === categoryName);
  return category?.CategoryID || null;
};

  const handleOpenModal = (asset: AssetType | null = null) => {
    if (asset) {
      setAssetId(asset.key);
      form.setFieldsValue({
        assetType: asset.type,
        Category: getCategoryIDByName(asset.category),
        title: asset.title,
        description: asset.description,
        isHighlighted: asset.isHighlighted,
        status: asset.status,
        thumbnail: asset.thumbnail,
        file: asset.url,
      });

      if (asset.url) {
        setVideoFile({
          uid: "-1",
          name: asset.url,
          status: "done",
        } as unknown as File);
      }
      if (asset.thumbnail) {
        setThumbnailFileList([
          {
            uid: "-1",
            name: asset.thumbnail,
            status: "done",
            url: `https://d20t6uqh7t6hqa.cloudfront.net/thumbnails/${asset.key}/${asset.thumbnail}`,
          },
        ]);
      }
    } else {
      setAssetId(null);
      form.resetFields();
      setVideoFile(null);
      setThumbnailFileList([]);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFileError(null);
    setAssetId(null);
    setVideoFile(null);
    setThumbnailFileList([]);
    form.resetFields();
    setThumbnailFileList([]);
  };

  const handleDelete = (assetID: number) => {
    setDeleteModalVisible(true);
    setDeleteIndex(assetID);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);

    try {
      dispatch(setLoading(true));
      const response = await AssetService.deleteAsset(deleteIndex);

      if (response.data) {
        dispatch(
          addNotification({
            title: "Success",
            description: response?.data?.message,
            type: "success",
          })
        );
        fetchAssets(currentPage, pageSize);
      } else {
        dispatch(
          addNotification({
            title: "Failed",
            description: "Failed to delete asset",
            type: "error",
          })
        );
      }
    } catch (error) {
      if (error?.response?.data?.error) {
        dispatch(
          addNotification({
            title: "Failed",
            description: error.response.data.error,
            type: "error",
          })
        );
      } else {
        dispatch(
          addNotification({
            title: "Failed",
            description: "An unexpected error occurred. Please try again.",
            type: "error",
          })
        );
      }
    } finally {
      setIsDeleting(false);
      dispatch(setLoading(false));
      setDeleteModalVisible(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleting(false);
    setDeleteModalVisible(false);
  };

  const handleFormSubmit = async () => {
    setIsSubmitting(true);

    try {
      const [values] = await Promise.all([
        form.validateFields(),
        new Promise((resolve, reject) => {
          if (!assetId && !videoFile) {
            setFileError("Please upload a file.");
            reject(new Error("File is required"));
          } else {
            resolve(true);
          }
        }),
      ]);

      await processFormSubmission(values);
    } catch (error) {
      if (error.errorFields) {
        return;
      } else if (error.message === "File is required") {
        return;
      } else {
        showNotification(
          "Failed",
          error?.response?.data?.error ||
            "An unexpected error occurred. Please try again.",
          "error"
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const processFormSubmission = async (values) => {
    if (!assetId && !videoFile) {
      handleMissingFiles();
      setIsSubmitting(false);
      return;
    }

    try {
      if (assetId) {
        await handleUpdateAsset(values);
      } else {
        await handleAddAsset(values);
      }
    } catch (error) {
      setIsSubmitting(false);
      throw error;
    }
  };

  const handleMissingFiles = () => {
    if (!videoFile) setFileError("Please upload a file.");
  };

const handleUpdateAsset = async (values) => {
  try {
    let thumbnailName = "";

    // Only set a new thumbnail name if a new file was uploaded
    if (thumbnailFileList.length > 0 && thumbnailFileList[0].originFileObj) {
      await uploadThumbnail(thumbnailFileList[0].originFileObj as File, assetId);
      thumbnailName = thumbnailFileList[0].name;
    } else if (thumbnailFileList.length > 0) {
      // Keep the existing thumbnail name if no new file was uploaded
      thumbnailName = thumbnailFileList[0].name;
    }

    const updateData = {
      Title: values.title,
      Description: values.description,
      Status: values.status === 1,
      IsHighlighted: values.isHighlighted,
      Thumbnail: thumbnailName, // This now preserves the original name
      Type: values.assetType,
      CategoryID: values.Category,
    };

    const updateResponse = await AssetService.updateAsset(assetId, updateData);
    const isSuccess = updateResponse.data;

    showNotification(
      isSuccess ? "Success" : "Failed",
      isSuccess ? updateResponse?.data?.message : "Failed to update asset",
      isSuccess ? "success" : "error"
    );

    if (isSuccess) {
      fetchAssets(currentPage, pageSize);
      resetForm();
    }
  } catch (error) {
    showNotification(
      "Failed",
      error?.response?.data?.message ||
        "An unexpected error occurred. Please try again.",
      "error"
    );
  } finally {
    dispatch(setLoading(false));
  }
};


  const handleAddAsset = async (values) => {
    const addResponse = (await AssetService.addAsset(
      prepareAssetData(values)
    )) as { data: AddAssetResponse };

    if (!addResponse.data.AssetID) {
      showNotification("Failed", "Failed to add asset", "error");
      return;
    }

    showNotification("Success", addResponse?.data?.message, "success");
    resetForm();
    fetchAssets(currentPage, pageSize);

    setTimeout(async () => {
      try {
        await handleFileUploads(addResponse.data.AssetID);
      } catch (uploadError) {
        showNotification(
          "Upload Failed",
          "File uploads completed with some errors",
          "error"
        );
      }
    }, 0);
  };

  const handleFileUploads = async (uploadAssetId: number) => {
    if (videoFile) await uploadVideo(videoFile, uploadAssetId);
    if (thumbnailFileList.length > 0)
      await uploadThumbnail(thumbnailFileList[0].originFileObj as File,uploadAssetId);
  };

  const showNotification = (title, description, type) => {
    dispatch(addNotification({ title, description, type }));
  };

  const prepareAssetData = (values) => ({
    Type: values.assetType,
    CategoryID: values.Category,
    Title: values.title,
    Thumbnail: thumbnailFileList[0]?.name || "",
    Description: values.description,
    URL: videoFile ? videoFile.name : undefined,
    IsHighlighted: values.isHighlighted || false,
    Status: values.status,
  });

  const resetForm = () => {
    form.resetFields();
    setVideoFile(null);
    handleCloseModal();
    setFileError(null);
    setThumbnailError(null);
    setThumbnailFileList([]);
  };

  const uploadVideo = async (file, assetID) => {
    try {
      const presignedUrlResponse = await videoUploadService.getPresignedUrl(
        file.name,
        file.type,
        assetID.toString()
      );

      if (!presignedUrlResponse.success) {
        return;
      }

      await videoUploadService.uploadFileToS3(
        file,
        presignedUrlResponse.uploadUrl
      );
    } catch (uploadError) {
      console.log("Video upload failed.");
    }
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setVideoFile(null);
  };

  const beforeUpload = (file: File) => {
    const videoTypes = ["video/mp4", "video/x-matroska", "video/webm"];
    const pdfTypes = ["application/pdf"];

    if (selectedCategory === "Video" && !videoTypes.includes(file.type)) {
      setFileError("Please upload a valid video file (MP4, MKV, WEBM).");
      return Upload.LIST_IGNORE;
    }

    if (selectedCategory === "PDF" && !pdfTypes.includes(file.type)) {
      setFileError("Please upload a valid PDF file.");
      return Upload.LIST_IGNORE;
    }

    setFileError(null);
    setVideoFile(file);
    return true;
  };

  const uploadThumbnail = async (file: File, assetID: number) => {
    try {
      const { uploadUrl } = await videoUploadService.getThumbnailPresignedUrl(
        assetID,
        file.name,
        file.type,
      );

      await videoUploadService.uploadFileToS3(file, uploadUrl);

      setThumbnailFileList([
        {
          uid: "-1",
          name: file.name,
          status: "done",
          url: URL.createObjectURL(file),
        },
      ]);

      form.setFieldsValue({ thumbnail: file.name });
    } catch (error) {
      console.log("Thumbnail upload failed");
    }
  };

  const handleThumbnailChange = ({ fileList }: UploadChangeParam) => {
    const updatedFileList = fileList.map((file) => {
      if (file.originFileObj instanceof Blob) {
        const fileName = file.name || `thumbnail_${Date.now()}.png`;
        const croppedFile = new File([file.originFileObj], fileName, {
          type: file.originFileObj.type || "image/png",
        });
        return { ...file, originFileObj: croppedFile };
      }
      return file;
    });

    setThumbnailFileList(updatedFileList);
    if (updatedFileList.length > 0) setThumbnailError(null);
  };
  const handleThumbnailPreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as File);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(
      file.name || file.url!.substring(file.url!.lastIndexOf("/") + 1)
    );
  };

  const getBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  return (
    <>
      <div className="vm__d-flex vm__h-100 vm__flex-column">
        <div className="flex-auto vm__mb-3">
          <div className="vm__container">
            <Row justify="space-between" align="middle">
              <h3 className="vm__page-header vm__mb-0">Assets</h3>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenModal}
              >
                New Asset
              </Button>
            </Row>
          </div>
        </div>
        <div className="flex-fill vm__overflow-auto">
          <div className="vm__container vm__h-100">
            <div className="vm__custom-table">
              <Table
                dataSource={assets}
                columns={columns}
                rowKey="key"
                scroll={{ x: true }}
                className="vm__table-container"
                pagination={
                  totalRecords > 10
                    ? {
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        onChange: (page, newPageSize) => {
                          setCurrentPage(page);
                          setPageSize(newPageSize);
                        },
                        position: ["bottomRight"],
                      }
                    : false
                }
              />
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmation
        isVisible={isDeleteModalVisible}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Confirm Deletion"
        content="Are you sure you want to delete this asset?"
        okText="Delete"
        confirmLoading={isDeleting}
      />

      <Modal
        title={assetId != null ? "Edit Asset" : "Add New Asset"}
        open={isModalOpen}
        onCancel={handleCloseModal}
        onOk={handleFormSubmit}
        okText="Submit"
        cancelText="Cancel"
        centered
        className="vm__modal vm_assests_component"
        width={600}
        maskClosable={false}
        confirmLoading={isSubmitting}
      >
        <div className="vm__h-100 vm__overflow-auto">
          <div className="vm__d-flex vm__flex-column vm__h-100 vm__scroll-h">
            <div className="vm__flex-fill vm__overflow-auto">
              <Form form={form} layout="vertical">
                <Form.Item
                  name="assetType"
                  label="Types"
                  rules={[{ required: true, message: "" }]}
                  className="vm__mb-2"
                >
                  <Select
                    placeholder="File type"
                    onChange={handleCategoryChange}
                    disabled={assetId != null}
                  >
                    {typeOptions.map((category, index) => (
                      <Option key={index} value={category}>
                        {category} 
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              <Form.Item
                name="Category"
                label="Category"
                rules={[{ required: true, message: "" }]}
                className="vm__mb-2"
              >
                <Select placeholder="Select category">
                  {assetCategories.map((category) => (
                    <Select.Option key={category.CategoryID} value={category.CategoryID}>
                      {category.CategoryName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

                <Form.Item
                  name="title"
                  label="Title"
                  rules={[{ required: true, message: "" }]}
                  className="vm__mb-2"
                >
                  <Input placeholder="Title" />
                </Form.Item>
                <Form.Item
                  name="description"
                  label="Description"
                  rules={[{ required: true, message: "" }]}
                  className="vm__mb-2"
                >
                  <TextArea style={{ height: 120, resize: "none" }} />
                </Form.Item>

                <Form.Item
                  name="isHighlighted"
                  valuePropName="checked"
                  className="vm__mb-2"
                >
                  <Checkbox>Highlight</Checkbox>
                </Form.Item>

                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: "" }]}
                  className="vm__mb-2"
                >
                  <Select placeholder="Select Status">
                    <Option value={1}>Publish</Option>
                    <Option value={0}>Unpublish</Option>
                  </Select>
                </Form.Item>
              </Form>
            </div>
            <div className="vm__flex-auto">
              <div className="vm__d-flex vm__gap-3">
                <div className="vm__flex-fill vm__min-w-0">
                  <div className="vm__d-flex vm__flex-column vm__h-100">
                    <div className="vm__flex-auto">
                      <Title level={5} className="vm__label-thumbnail">
                        Drag / Upload File{" "}
                        <sup className="vm__mandatory-text">*</sup>
                      </Title>
                    </div>
                    <div className="vm__flex-fill">
                      <Form.Item
                        validateStatus={fileError ? "error" : ""}
                        help={fileError}
                        required
                        className="vm__mb-0 vm__custom-py-1"
                      >
                        <Dragger
                          name="file"
                          multiple={false}
                          fileList={
                            videoFile
                              ? [
                                  {
                                    uid: "-1",
                                    name:
                                      typeof videoFile === "string"
                                        ? videoFile
                                        : videoFile.name,
                                    status: "done",
                                  },
                                ]
                              : []
                          }
                          beforeUpload={beforeUpload}
                          onRemove={() => {
                            setVideoFile(null);
                            setFileError(null);
                          }}
                          className="vm__dragger vm__w-100"
                          disabled={assetId != null}
                        >
                          <p className="ant-upload-drag-icon">
                            <CloudUploadOutlined />
                          </p>
                          <p className="ant-upload-text">
                            Click or drag file to this area to upload
                          </p>
                        </Dragger>
                      </Form.Item>
                    </div>
                  </div>
                </div>
                <div className="vm__flex-auto vm__min-w-0 vm__thumbnail-container">
                  <div className="vm__d-flex vm__flex-column vm__h-100">
                    <div className="vm__flex-auto">
                      <Title level={5} className="vm__label-thumbnail">
                        Upload Thumbnail{" "}
                        {/* <sup className="vm__mandatory-text">*</sup> */}
                      </Title>
                    </div>
                    <div className="vm__flex-fill">
                      <Form.Item
                        name="thumbnail"
                        validateStatus={thumbnailError ? "error" : ""}
                        help={thumbnailError}
                        className="vm__mb-0"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => {
                          if (Array.isArray(e)) {
                            return e;
                          }
                          return e?.fileList;
                        }}
                      >
                        <ImgCrop rotationSlider>
                          <Upload
                            listType="picture-card"
                            fileList={thumbnailFileList}
                            onChange={handleThumbnailChange}
                            onPreview={handleThumbnailPreview}
                            onRemove={() => setThumbnailFileList([])}
                            className="vm__dragger"
                            style={{ width: 200, height: 200 }}
                          >
                            {thumbnailFileList.length === 0 && (
                              <div className="ant-upload-drag-container">
                                <p className="ant-upload-drag-icon">
                                  <FileImageOutlined />
                                </p>
                                <p className="ant-upload-text">
                                  Click or drag image to this area to upload
                                </p>
                              </div>
                            )}
                          </Upload>
                        </ImgCrop>
                      </Form.Item>
                    </div>
                  </div>

                  <Modal
                    open={previewOpen}
                    title={previewTitle}
                    footer={null}
                    onCancel={() => setPreviewOpen(false)}
                  >
                    <img
                      alt="Thumbnail preview"
                      style={{ width: "100%" }}
                      src={previewImage}
                    />
                  </Modal>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
export default Assets;
